import React from 'react';

import { gql, useQuery } from '@apollo/client';
import { AccountBalanceWallet, AddCard, ControlPointDuplicate, Refresh } from '@mui/icons-material';
import { Button, Card, CardContent, Container, Divider, IconButton, Stack, Typography } from '@mui/material';
import dayjs from 'dayjs';
import { get, isNil, map, range } from 'lodash';
import numeral from 'numeral';
import { Link } from 'react-router-dom';
import { LoadingSpinner, useTrackPageView } from '../../../components';
import { createScopedI18n } from '../../../i18n/i18n';
import { MonthlyTransactionAccordion } from './monthly_transaction_accordion';

const creditPageI18n = createScopedI18n('pages.credit');

const getCreditAmountGql = gql(`
  query getCreditAmount {
    user {
      id
      chain {
        id
        creditAmount
        debitAmount
        sumHiringsCharge
      }
    }
  }
`);

const CreditPage = () => {
  useTrackPageView('CreditPage');

  const monthList = React.useMemo(
    () => map(range(0, 5), (monthOffset) => dayjs().subtract(monthOffset, 'months').startOf('month')),
    [],
  );

  const { data, loading, refetch } = useQuery(getCreditAmountGql, {
    fetchPolicy: 'network-only',
    notifyOnNetworkStatusChange: true,
  });

  const chain: {
    id: string;
    creditAmount: number | null;
    debitAmount: number | null;
    sumHiringsCharge: number | null;
  } | null = get(data, ['user', 'chain'], null);

  return (
    <Container maxWidth="sm" sx={{ paddingY: 2 }}>
      <Stack gap={2}>
        <Card elevation={3}>
          <CardContent>
            <Stack flexDirection="row" alignItems="center" height={24} gap={1}>
              <Typography flex={1}>{creditPageI18n('pending_hiring_charge')}</Typography>

              {loading ? (
                <LoadingSpinner size={24} BoxProps={{ flex: 0, marginX: 2 }} />
              ) : (
                <Typography variant="h6">{numeral(chain?.sumHiringsCharge).format('0,0')}</Typography>
              )}
            </Stack>
          </CardContent>
        </Card>

        {isNil(chain?.debitAmount) ? null : (
          <Card elevation={3}>
            <CardContent sx={{ height: 200, display: 'flex', flexDirection: 'column' }}>
              <Stack flexDirection="row" alignItems="center" gap={1}>
                <AccountBalanceWallet />
                <Typography flex={1}>{creditPageI18n('debit_wallet')}</Typography>
                <Button
                  variant="outlined"
                  size="small"
                  component={Link}
                  to={'purchase'}
                  startIcon={<ControlPointDuplicate />}
                >
                  {creditPageI18n('purchase')}
                </Button>
                <IconButton onClick={() => refetch()}>
                  <Refresh />
                </IconButton>
              </Stack>

              {loading ? (
                <LoadingSpinner />
              ) : (
                <Stack flex={1} justifyContent="center" alignItems="center">
                  <Typography variant="h3">{numeral(chain?.debitAmount).format('0,0.00')}</Typography>
                </Stack>
              )}
            </CardContent>
          </Card>
        )}

        {isNil(chain?.creditAmount) ? null : (
          <Card elevation={3}>
            <CardContent sx={{ height: 200, display: 'flex', flexDirection: 'column' }}>
              <Stack flexDirection="row" alignItems="center" gap={1}>
                <AddCard />
                <Typography flex={1}>{creditPageI18n('credit_wallet')}</Typography>
                <IconButton onClick={() => refetch()}>
                  <Refresh />
                </IconButton>
              </Stack>

              {loading ? (
                <LoadingSpinner />
              ) : (
                <Stack flex={1} justifyContent="center" alignItems="center">
                  <Typography variant="h3">{numeral(chain?.creditAmount).format('0,0.00')}</Typography>
                </Stack>
              )}
            </CardContent>
          </Card>
        )}

        <Divider />
        <Typography variant="subtitle1" paddingLeft={3}>
          {creditPageI18n('transaction_history')}
        </Typography>
        <Stack>
          {map(monthList, (month) => (
            <MonthlyTransactionAccordion key={month.month()} year={month.year()} month={month.month() + 1} />
          ))}
        </Stack>
      </Stack>
    </Container>
  );
};

export default CreditPage;
